import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditorStore } from '@/store/editorStore';
import { connectionsApi } from '@/api/connections';
import { importApi, type AnalyzeResult, type ImportJobStatus } from '@/api/import';
import type { ColumnDesignInfo, Connection } from '@/types/domain';
import './ImportModal.css';

interface Props { onClose: () => void; }

type Step = 'setup' | 'mapping' | 'progress';

export function ImportModal({ onClose }: Props) {
  const connections = useEditorStore(s => s.connections);
  const selectedConnectionId = useEditorStore(s => s.selectedConnectionId);

  const [step, setStep] = useState<Step>('setup');
  const [connId, setConnId] = useState(selectedConnectionId ?? '');
  const [schema, setSchema] = useState('');
  const [table, setTable] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeResult, setAnalyzeResult] = useState<AnalyzeResult | null>(null);
  const [tableColumns, setTableColumns] = useState<ColumnDesignInfo[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [fixedValues, setFixedValues] = useState<Array<{ tableCol: string; value: string }>>([]);
  const [aiMapped, setAiMapped] = useState(false);
  const [suggestingAi, setSuggestingAi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<ImportJobStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const _selectedConn: Connection | undefined = connections.find(c => c.id === connId);
  void _selectedConn;

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    pollRef.current = setInterval(async () => {
      try {
        const status = await importApi.getJob(jobId);
        setJobStatus(status);
        if (status.status !== 'running') clearInterval(pollRef.current!);
      } catch { /* ignore */ }
    }, 800);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobId]);

  const handleAnalyze = useCallback(async () => {
    if (!file || !connId || !schema || !table) return;
    setError(null);
    setAnalyzing(true);
    setDefaults({});
    setFixedValues([]);
    try {
      // Analyze file (required — stops here if this fails)
      const result = await importApi.analyze(connId, file);
      setAnalyzeResult(result);

      // Fetch table columns for auto-mapping (optional — proceed even if it fails)
      let cols: typeof tableColumns = [];
      try {
        cols = await connectionsApi.getTableDesign(connId, schema, table);
        setTableColumns([...cols].sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        setTableColumns([]);
      }

      setStep('mapping');

      // After mapping is resolved, auto-populate fixedValues with required unmapped columns
      const defaultForType = (dataType: string): string => {
        const t = dataType.toLowerCase();
        if (t === 'boolean' || t === 'bool' || t === 'bit') return 'false';
        if (t.includes('timestamp') || t.includes('datetime') || t === 'date' || t.includes('time')) return 'now';
        if (t.includes('int') || t.includes('numeric') || t.includes('decimal') ||
            t.includes('float') || t === 'real' || t.includes('money') || t.includes('double')) return '0';
        return '';
      };
      const populateRequiredFixed = (resolvedMapping: Record<string, string>) => {
        const mappedCols = new Set(Object.values(resolvedMapping).filter(Boolean).map(v => v.toLowerCase()));
        const requiredUnmapped = cols.filter(c => !c.nullable && !c.isPrimaryKey && c.defaultValue === null && !mappedCols.has(c.name.toLowerCase()));
        if (requiredUnmapped.length > 0)
          setFixedValues(requiredUnmapped.map(c => ({ tableCol: c.name, value: defaultForType(c.dataType) })));
      };

      // Call AI to suggest mapping (runs after entering step 2)
      if (cols.length > 0) {
        setSuggestingAi(true);
        try {
          const { mapping: aiMap, aiUsed } = await importApi.suggestMapping(
            result.headers,
            result.sample,
            cols.map(c => ({ name: c.name, dataType: c.dataType, nullable: c.nullable }))
          );
          setMapping(aiMap);
          setAiMapped(aiUsed);
          populateRequiredFixed(aiMap);
        } catch {
          // Fallback: exact name match
          const autoMap: Record<string, string> = {};
          for (const fCol of result.headers) {
            const match = cols.find(tc => tc.name.toLowerCase() === fCol.toLowerCase());
            if (match) autoMap[fCol] = match.name;
          }
          setMapping(autoMap);
          populateRequiredFixed(autoMap);
        } finally {
          setSuggestingAi(false);
        }
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ?? (err as Error)?.message ?? 'Erro ao analisar arquivo';
      setError(msg);
    } finally {
      setAnalyzing(false);
    }
  }, [file, connId, schema, table]);

  const handleRetry = useCallback(() => {
    setJobId(null);
    setJobStatus(null);
    setStep('mapping');
  }, []);

  const addFixed = () => setFixedValues(fv => [...fv, { tableCol: '', value: '' }]);
  const removeFixed = (i: number) => setFixedValues(fv => fv.filter((_, idx) => idx !== i));
  const updateFixed = (i: number, field: 'tableCol' | 'value', val: string) =>
    setFixedValues(fv => fv.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const handleImport = useCallback(async () => {
    if (!file || !connId || !schema || !table) return;
    setError(null);

    // Validate: required columns must have a file mapping (with null fallback if needed) OR a non-empty fixed value
    const fixedMap: Record<string, string> = {};
    for (const fv of fixedValues) if (fv.tableCol && fv.value !== '') fixedMap[fv.tableCol] = fv.value;
    const mappedTableCols = new Set(Object.values(mapping).filter(Boolean).map(v => v.toLowerCase()));
    const uncovered = tableColumns.filter(c => {
      if (c.nullable) return false;
      if (c.isPrimaryKey) return false;
      if (c.defaultValue !== null) return false;
      if (mappedTableCols.has(c.name.toLowerCase())) return false;
      if (fixedMap[c.name] !== undefined) return false;
      return true;
    });
    if (uncovered.length > 0) {
      setError(`Colunas obrigatórias sem valor: ${uncovered.map(c => c.name).join(', ')}. Adicione em "Campos com valor fixo" ou mapeie do arquivo.`);
      return;
    }

    setStep('progress');
    try {
      const { jobId: id } = await importApi.start(connId, file, schema, table, mapping, fixedMap, defaults);
      setJobId(id);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ?? (err as Error)?.message ?? 'Erro ao iniciar importação';
      setError(msg);
      setStep('mapping');
    }
  }, [file, connId, schema, table, mapping, fixedValues, defaults, tableColumns]);

  const progress = jobStatus && jobStatus.totalRows > 0
    ? Math.round((jobStatus.processedRows / jobStatus.totalRows) * 100)
    : 0;

  return (
    <div className="im-overlay">
      <div className="im-panel">
        <div className="im-header">
          <span className="im-title">↑ Importar arquivo</span>
          <button className="im-close" onClick={onClose}>×</button>
        </div>

        <div className="im-steps">
          <span className={`im-step ${step === 'setup' ? 'active' : 'done'}`}>1 Arquivo</span>
          <span className="im-step-sep">›</span>
          <span className={`im-step ${step === 'mapping' ? 'active' : step === 'progress' ? 'done' : ''}`}>2 Mapeamento</span>
          <span className="im-step-sep">›</span>
          <span className={`im-step ${step === 'progress' ? 'active' : ''}`}>3 Importação</span>
        </div>

        <div className="im-body">
          {/* ── Step 1: Setup ──────────────────────────────────────── */}
          {step === 'setup' && (
            <div className="im-section">
              <div className="im-field">
                <label className="im-label">Conexão</label>
                <select className="im-select" value={connId} onChange={e => setConnId(e.target.value)}>
                  <option value="">— selecione —</option>
                  {connections.map(c => (
                    <option key={c.id} value={c.id}>{c.name} / {c.database}</option>
                  ))}
                </select>
              </div>
              <div className="im-row">
                <div className="im-field">
                  <label className="im-label">Schema</label>
                  <input className="im-input" value={schema} onChange={e => setSchema(e.target.value)} placeholder="dbo" />
                </div>
                <div className="im-field">
                  <label className="im-label">Tabela destino</label>
                  <input className="im-input" value={table} onChange={e => setTable(e.target.value)} placeholder="nome_da_tabela" />
                </div>
              </div>
              <div className="im-field">
                <label className="im-label">Arquivo (CSV, TXT, TSV, XLSX, XLS)</label>
                <div className="im-file-zone" onClick={() => fileRef.current?.click()}>
                  {file ? (
                    <><span className="im-file-name">{file.name}</span><span className="im-file-size">{(file.size / 1024).toFixed(0)} KB</span></>
                  ) : (
                    <span className="im-file-placeholder">Clique para selecionar ou arraste o arquivo aqui</span>
                  )}
                </div>
                <input
                  ref={fileRef} type="file" hidden
                  accept=".csv,.txt,.tsv,.xlsx,.xls"
                  onChange={e => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
              {error && <div className="im-error">{error}</div>}
            </div>
          )}

          {/* ── Step 2: Column mapping ────────────────────────────── */}
          {step === 'mapping' && analyzeResult && (
            <div className="im-section">
              <div className="im-mapping-header">
            <p className="im-hint">{analyzeResult.headers.length} colunas detectadas. Mapeie cada coluna do arquivo para uma coluna da tabela <strong>{schema}.{table}</strong>.</p>
            {suggestingAi && <span className="im-ai-badge loading">✦ IA mapeando...</span>}
            {!suggestingAi && aiMapped && <span className="im-ai-badge done">✦ Mapeado por IA</span>}
            {!suggestingAi && !aiMapped && Object.keys(mapping).length > 0 && (
              <span className="im-ai-badge done" style={{ color: 'var(--text-muted)', background: 'rgba(255,255,255,0.05)' }}>~ Mapeamento automático</span>
            )}
          </div>
              <table className="im-mapping-table">
                <thead>
                  <tr>
                    <th>Coluna do arquivo</th>
                    <th>Amostra</th>
                    <th>→</th>
                    <th>Coluna da tabela</th>
                    <th>Valor se nulo</th>
                  </tr>
                </thead>
                <tbody>
                  {analyzeResult.headers.map((fCol, i) => {
                    const mappedTc = tableColumns.find(c => c.name === (mapping[fCol] ?? ''));
                    const isRequired = mappedTc && !mappedTc.nullable;
                    return (
                      <tr key={fCol}>
                        <td className="im-col-file">{fCol}</td>
                        <td className="im-col-sample">{analyzeResult.sample[0]?.[i] ?? ''}</td>
                        <td className="im-col-arrow">→</td>
                        <td>
                          <select
                            className="im-select-sm"
                            value={mapping[fCol] ?? ''}
                            onChange={e => setMapping(m => ({ ...m, [fCol]: e.target.value }))}
                          >
                            <option value="">— ignorar —</option>
                            {tableColumns.map(tc => (
                              <option key={tc.name} value={tc.name}>{tc.name} ({tc.dataType})</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {mapping[fCol] ? (
                            <input
                              className="im-input-sm"
                              value={defaults[fCol] ?? ''}
                              onChange={e => setDefaults(d => ({ ...d, [fCol]: e.target.value }))}
                              placeholder={isRequired ? 'obrigatório *' : '—'}
                              style={isRequired && !defaults[fCol] ? { borderColor: 'rgba(255,100,100,0.4)' } : undefined}
                            />
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Fixed / constant values section */}
              <div className="im-fixed-section">
                <div className="im-fixed-header">
                  <span className="im-label">Campos com valor fixo</span>
                  <button className="im-btn-add" onClick={addFixed}>+ Adicionar campo</button>
                </div>
                {fixedValues.map((fv, i) => (
                  <div key={i} className="im-fixed-row">
                    <select
                      className="im-select-sm"
                      value={fv.tableCol}
                      onChange={e => updateFixed(i, 'tableCol', e.target.value)}
                    >
                      <option value="">— coluna da tabela —</option>
                      {tableColumns.map(tc => {
                        const alreadyMapped = fv.tableCol !== tc.name && Object.values(mapping).includes(tc.name);
                        return (
                          <option key={tc.name} value={tc.name} disabled={alreadyMapped}>
                            {tc.name} ({tc.dataType}){alreadyMapped ? ' — já mapeado' : ''}
                          </option>
                        );
                      })}
                    </select>
                    <span className="im-fixed-eq">=</span>
                    <input
                      className="im-input-sm"
                      value={fv.value}
                      onChange={e => updateFixed(i, 'value', e.target.value)}
                      placeholder={fv.tableCol && tableColumns.find(c => c.name === fv.tableCol && !c.nullable) ? 'obrigatório *' : 'valor fixo para todas as linhas'}
                      style={fv.tableCol && !fv.value && tableColumns.find(c => c.name === fv.tableCol && !c.nullable) ? { borderColor: 'rgba(255,100,100,0.4)' } : undefined}
                    />
                    <button className="im-fixed-remove" onClick={() => removeFixed(i)}>×</button>
                  </div>
                ))}
              </div>

              {error && <div className="im-error">{error}</div>}
            </div>
          )}

          {/* ── Step 3: Progress ──────────────────────────────────── */}
          {step === 'progress' && (
            <div className="im-section im-progress-section">
              {!jobStatus && <div className="im-loading">Iniciando...</div>}
              {jobStatus && (
                <>
                  <div className="im-progress-label">
                    {jobStatus.status === 'running' && `Processando... ${jobStatus.processedRows.toLocaleString()} / ${jobStatus.totalRows > 0 ? jobStatus.totalRows.toLocaleString() : '?'} linhas`}
                    {jobStatus.status === 'done' && 'Importação concluída!'}
                    {jobStatus.status === 'error' && 'Erro na importação'}
                  </div>
                  <div className="im-progress-bar-wrap">
                    <div
                      className={`im-progress-bar ${jobStatus.status === 'done' ? 'done' : jobStatus.status === 'error' ? 'error' : ''}`}
                      style={{ width: jobStatus.status === 'done' ? '100%' : `${progress}%` }}
                    />
                  </div>
                  {jobStatus.status !== 'running' && (
                    <div className="im-result-stats">
                      <span className="im-stat success">✓ {jobStatus.insertedRows.toLocaleString()} inseridas</span>
                      {jobStatus.errorRows > 0 && <span className="im-stat error">✗ {jobStatus.errorRows.toLocaleString()} erros</span>}
                    </div>
                  )}
                  {jobStatus.sampleErrors.length > 0 && (
                    <div className="im-errors-list">
                      {jobStatus.sampleErrors.map((e, i) => <div key={i} className="im-error-item">{e}</div>)}
                    </div>
                  )}
                  {jobStatus.errorMessage && <div className="im-error">{jobStatus.errorMessage}</div>}
                </>
              )}
            </div>
          )}
        </div>

        <div className="im-footer">
          {step === 'setup' && (
            <>
              <button className="im-btn" onClick={onClose}>Cancelar</button>
              <button
                className="im-btn primary"
                disabled={!file || !connId || !schema || !table || analyzing}
                onClick={handleAnalyze}
              >
                {analyzing ? 'Analisando...' : 'Analisar arquivo →'}
              </button>
            </>
          )}
          {step === 'mapping' && (
            <>
              <button className="im-btn" onClick={() => { setStep('setup'); setFixedValues([]); setDefaults({}); }}>← Voltar</button>
              <button
                className="im-btn primary"
                disabled={Object.values(mapping).filter(Boolean).length === 0}
                onClick={handleImport}
              >
                Importar →
              </button>
            </>
          )}
          {step === 'progress' && jobStatus?.status !== 'running' && (
            <>
              {(jobStatus?.status === 'error' || (jobStatus?.errorRows ?? 0) > 0) && (
                <button className="im-btn" onClick={handleRetry}>← Corrigir mapeamento</button>
              )}
              <button className="im-btn primary" onClick={onClose}>Fechar</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
